/**
 * DepositMarkPaidModal — บันทึกรับมัดจำ (PENDING → PAID) — Deposit v2 Phase 1
 * modal เล็ก: วันที่รับมัดจำ (default วันนี้, ≤ วันนี้) → POST .../deposit/mark-paid {paid_date}
 */
import React, { useEffect, useState } from 'react';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import { cn } from '../../utils/cn';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { getLocalDateString } from '../../utils/dateUtils';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { useMarkDepositPaid } from '../../hooks/mutations/useDepositMutations';
import { DepositActionResponse } from '../../services/depositService';
import { DepositModalItem, getDepositItemName, getDepositItemSerial } from './DepositReturnModal';

interface DepositMarkPaidModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalId: number;
  item: DepositModalItem | null;
  onSuccess?: (response: DepositActionResponse) => void;
}

const DepositMarkPaidModal: React.FC<DepositMarkPaidModalProps> = ({ isOpen, onClose, rentalId, item, onSuccess }) => {
  const [paidDate, setPaidDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const markPaidMutation = useMarkDepositPaid();
  const submitGuard = useSubmitGuard();
  const today = getLocalDateString();

  useEffect(() => {
    if (isOpen) {
      setPaidDate(getLocalDateString());
      setError(null);
    }
  }, [isOpen, item?.id]);

  const loading = markPaidMutation.isPending;

  const handleSubmit = async () => {
    if (!item) return;
    if (!paidDate) {
      setError('กรุณาระบุวันที่รับมัดจำ');
      return;
    }
    if (paidDate > today) {
      setError('วันที่รับมัดจำต้องไม่เกินวันนี้');
      return;
    }
    if (!submitGuard.begin()) return;
    try {
      const response = await markPaidMutation.mutateAsync({ rentalId, itemId: item.id, data: { paid_date: paidDate } });
      onSuccess?.(response);
      onClose();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'ไม่สามารถบันทึกรับมัดจำได้'));
    } finally {
      submitGuard.end();
    }
  };

  if (!item) return null;

  const serial = getDepositItemSerial(item);
  const amount = Number(item.deposit_amount || 0);

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        disabled={loading}
      >
        ยกเลิก
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-success-600 border border-transparent rounded-md hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success-500 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={loading}
      >
        {loading ? 'กำลังบันทึก...' : 'บันทึกรับมัดจำ'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="บันทึกรับมัดจำ"
      size="sm"
      icon={<RiMoneyDollarCircleLine className="w-5 h-5" />}
      footer={footer}
      closeOnClickOutside={!loading}
      closeOnEsc={!loading}
    >
      <div className="space-y-4">
        <div className="bg-secondary-50 rounded-lg p-3 text-sm space-y-1">
          <p className="font-medium text-secondary-900 break-words">{getDepositItemName(item)}</p>
          {serial && (
            <p className="text-secondary-600">
              Serial Number: <span className="font-mono">{serial}</span>
            </p>
          )}
          <p className="text-secondary-600">
            ยอดมัดจำ: <span className="font-semibold text-secondary-900">฿{amount.toLocaleString()}</span>
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700" role="alert">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="deposit-paid-date" className="block text-sm font-medium text-secondary-700">
            วันที่รับมัดจำ <span className="text-danger-500">*</span>
          </label>
          <input
            id="deposit-paid-date"
            type="date"
            value={paidDate}
            max={today}
            onChange={(e) => {
              setPaidDate(e.target.value);
              setError(null);
            }}
            disabled={loading}
            className={cn('input mt-1', loading && 'bg-secondary-50 cursor-not-allowed')}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DepositMarkPaidModal;
